import { useStableCallback } from '@main/shared/browser';
import { createSorter } from '@main/shared/utils/sortable';
import {
  FormHTMLAttributes,
  ForwardedRef,
  forwardRef,
  memo,
  ReactNode,
  RefAttributes,
  RefObject,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import {
  FieldValues,
  FormProvider,
  SubmitHandler,
  useForm,
  UseFormProps,
  UseFormReturn,
} from 'react-hook-form';

import { DynamicFormContext, DynamicFormContextType } from './context';
import { DynamicField, DynamicFieldWrapper, DynamicFormErrorMessage } from './dynamic-field';
import { AnyDynamicFormComponentRegistry, DynamicFormFieldConfig } from './field-registry';
import { defaultItemsRender, DynamicFormItemsRender } from './items-render';
import { DynamicFieldStateInput } from './types';

export interface DynamicFormProps<
  TRegistry extends AnyDynamicFormComponentRegistry,
  TWrapperProps extends object = object,
> {
  componentRegistry: TRegistry;
  fields: readonly DynamicFormFieldConfig<TRegistry>[];
  onSubmit?: SubmitHandler<FieldValues>;
  form?: Omit<FormHTMLAttributes<HTMLFormElement>, 'onSubmit'>;
  useForm?: UseFormProps;
  children?: ReactNode;
  /** What the whole form is: View mode is `{ isReadOnly: true }`. */
  fieldState?: DynamicFieldStateInput;
  /** Turns a failed rule into its message; defaults to the error's own message. */
  errorMessage?: DynamicFormErrorMessage<TRegistry>;
  fieldWrapper?: DynamicFieldWrapper<TRegistry, TWrapperProps>;
  fieldWrapperProps?: TWrapperProps;
  fieldSortDir?: 'asc' | 'desc';
  itemsRender?: DynamicFormItemsRender<TRegistry>;
}

export interface DynamicFormRef {
  form: UseFormReturn;
  formRef: RefObject<HTMLFormElement>;
}

function DynamicForm_<
  TRegistry extends AnyDynamicFormComponentRegistry,
  TWrapperProps extends object = object,
>(props: DynamicFormProps<TRegistry, TWrapperProps>, ref: ForwardedRef<DynamicFormRef>) {
  const form = useForm(props.useForm);
  const formRef = useRef<HTMLFormElement>(null);
  const ItemsRender: DynamicFormItemsRender<TRegistry> = props.itemsRender ?? defaultItemsRender;
  const fromContext = useMemo(
    () =>
      ({
        wrapper: props.fieldWrapper,
        wrapperProps: props.fieldWrapperProps,
        fieldState: props.fieldState,
        errorMessage: props.errorMessage,
        componentRegistry: props.componentRegistry,
        itemsRender: ItemsRender,
      }) as DynamicFormContextType,
    [
      props.componentRegistry,
      props.fieldState,
      props.errorMessage,
      props.fieldWrapper,
      props.fieldWrapperProps,
      ItemsRender,
    ],
  );

  const fieldsSorter = useMemo(
    () => (props.fieldSortDir ? createSorter(props.fieldSortDir) : undefined),
    [props.fieldSortDir],
  );
  const fields = useMemo(
    () => (fieldsSorter ? props.fields.toSorted(fieldsSorter) : props.fields),
    [props.fields, fieldsSorter],
  );

  useImperativeHandle(ref, () => ({ form, formRef }), [form, formRef]);

  const onSubmit = form.handleSubmit(props.onSubmit ?? (() => null));

  const itemRender = useStableCallback((item: DynamicFormFieldConfig<TRegistry>) => (
    <DynamicField
      config={item}
      wrapper={props.fieldWrapper}
      wrapperProps={props.fieldWrapperProps}
      fieldState={props.fieldState}
      errorMessage={props.errorMessage}
      componentRegistry={props.componentRegistry}
    />
  ));

  return (
    <DynamicFormContext.Provider value={fromContext}>
      <FormProvider {...form}>
        <form {...props.form} onSubmit={onSubmit} ref={formRef}>
          <ItemsRender fields={fields} itemRender={itemRender} />
          {props.children}
        </form>
      </FormProvider>
    </DynamicFormContext.Provider>
  );
}

export const DynamicForm = memo(forwardRef(DynamicForm_)) as <
  TRegistry extends AnyDynamicFormComponentRegistry,
  TWrapperProps extends object = object,
>(
  props: DynamicFormProps<TRegistry, TWrapperProps> & RefAttributes<DynamicFormRef>,
) => ReactNode;
